/**
 * useChatbotConfig — single source of truth for the chatbot's identity
 * across the whole SPA. Fetched once on boot from `/api/chatbot/config`
 * and cached in React Query with a long stale-time. Every consumer of the
 * chatbot name (widget header, Sancta Commerce shell, admin labels,
 * static-copy interpolation) reads from here so a rename in the admin
 * console propagates everywhere without any hardcoding.
 */
import { useQuery } from "@tanstack/react-query";

export interface ChatbotConfig {
  name: string;
  tagline: string;
  greeting: string;
  returning_greeting: string;
  accent_color: string;
  emoji_prefix: string;
}

const BACKEND = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

const FALLBACK: ChatbotConfig = {
  name: "Sancta",
  tagline: "Your always-on shopping concierge",
  greeting:
    "Hi 👋 I'm {name}. I can help you find products, compare options, " +
    "explain SibaScores, and even complete your order — all right here. " +
    "What are you looking for today?",
  returning_greeting:
    "Welcome back ✨ Great to see you again. I remember what we were " +
    "looking at last time — want me to pick up from there?",
  accent_color: "#f97316",
  emoji_prefix: "✨",
};

async function fetchConfig(): Promise<ChatbotConfig> {
  try {
    const resp = await fetch(`${BACKEND}/api/chatbot/config`, { credentials: "omit" });
    if (!resp.ok) throw new Error(String(resp.status));
    const j = await resp.json();
    return { ...FALLBACK, ...j };
  } catch {
    return FALLBACK;
  }
}

export function useChatbotConfig() {
  const q = useQuery<ChatbotConfig>({
    queryKey: ["chatbot-config"],
    queryFn: fetchConfig,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  });
  return q.data || FALLBACK;
}

/** Interpolate `{name}` and `{emoji}` placeholders inside static copy. */
export function interpolate(text: string, cfg: ChatbotConfig): string {
  return text
    .replace(/\{name\}/g, cfg.name)
    .replace(/\{emoji\}/g, cfg.emoji_prefix)
    .replace(/\{tagline\}/g, cfg.tagline);
}

/** Convenience — just the current name. */
export function useChatbotName(): string {
  return useChatbotConfig().name;
}
